/**
 * 音频管理器统一入口
 */
import { AudioManager } from './audio-manager';
import { MusicManagerAdaptive } from './music-manager-adaptive';
import { SoundEffectManagerAdaptive } from './sound-effect-manager-adaptive';
import type { AudioManagerOptions } from './types';

/**
 * 统一音频管理器的初始化选项
 */
export interface UnifiedAudioManagerOptions extends AudioManagerOptions {
  /**
   * 是否自动解锁音频（在用户交互时）
   * @default false
   */
  autoUnlock?: boolean;

  /**
   * 自动解锁监听的事件类型
   * @default ['click', 'touchstart']
   */
  autoUnlockEvents?: string[];

  /**
   * 自动解锁成功回调
   */
  onAutoUnlockSuccess?: () => void;

  /**
   * 自动解锁失败回调
   */
  onAutoUnlockError?: (error: Error) => void;
}

/**
 * 统一的音频管理器类
 * 自动选择最佳实现（Web Audio API 或 HTML5 Audio）
 */
export class UnifiedAudioManager {
  public readonly audioManager: AudioManager;
  public readonly musicManager: MusicManagerAdaptive;
  public readonly soundEffectManager: SoundEffectManagerAdaptive;

  private unlockListenerAdded: boolean = false;

  constructor(options: UnifiedAudioManagerOptions = {}) {
    this.audioManager = new AudioManager(options);
    this.musicManager = new MusicManagerAdaptive(this.audioManager);
    this.soundEffectManager = new SoundEffectManagerAdaptive(this.audioManager);

    // 如果启用自动解锁，立即注册监听器
    if (options.autoUnlock) {
      this.autoUnlockOnUserInteraction({
        events: options.autoUnlockEvents,
        onSuccess: options.onAutoUnlockSuccess,
        onError: options.onAutoUnlockError,
      });
    }
  }

  /**
   * 初始化音频系统
   */
  init(options: AudioManagerOptions = {}): void {
    this.audioManager.init(options);
  }

  /**
   * 更新所有管理器的音量（响应全局开关或音量变化）
   */
  updateVolume(): void {
    this.musicManager.updateVolume();
    this.soundEffectManager.updateVolume();
  }

  /**
   * 统一解锁音频（兼容 Safari 等浏览器的自动播放限制）
   * 同时解锁音乐和音效的 AudioContext
   * @returns 是否解锁成功
   */
  async unlockAudio(): Promise<boolean> {
    try {
      console.log('[UnifiedAudioManager] Unlocking audio contexts...');

      const results = await Promise.allSettled([
        this.musicManager.unlockAudioContext(),
        this.soundEffectManager.unlockAudioContext(),
      ]);

      const musicUnlocked = results[0].status === 'fulfilled' && results[0].value;
      const soundEffectUnlocked = results[1].status === 'fulfilled' && results[1].value;

      if (musicUnlocked && soundEffectUnlocked) {
        console.log('[UnifiedAudioManager] Audio contexts unlocked successfully');
        return true;
      } else {
        console.warn('[UnifiedAudioManager] Some audio contexts failed to unlock', {
          music: musicUnlocked,
          soundEffect: soundEffectUnlocked,
        });
        return false;
      }
    } catch (error) {
      console.error('[UnifiedAudioManager] Failed to unlock audio:', error);
      return false;
    }
  }

  /**
   * 自动在用户交互时解锁音频
   * 会自动监听第一次点击/触摸事件
   * @param options 配置选项
   * @param options.events 要监听的事件类型，默认 ['click', 'touchstart']
   * @param options.onSuccess 解锁成功回调
   * @param options.onError 解锁失败回调
   */
  autoUnlockOnUserInteraction(options: {
    events?: string[];
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}): void {
    if (this.unlockListenerAdded) {
      console.warn('[UnifiedAudioManager] Auto unlock listener already added');
      return;
    }

    const events = options.events || ['click', 'touchstart'];

    const unlockHandler = async () => {
      try {
        const success = await this.unlockAudio();

        if (success) {
          options.onSuccess?.();
        } else {
          options.onError?.(new Error('Failed to unlock audio'));
        }

        // 移除监听器
        events.forEach(event => {
          document.removeEventListener(event, unlockHandler);
        });
        this.unlockListenerAdded = false;
      } catch (error) {
        options.onError?.(error as Error);
      }
    };

    // 添加监听器
    events.forEach(event => {
      document.addEventListener(event, unlockHandler, { once: true });
    });

    this.unlockListenerAdded = true;
    console.log('[UnifiedAudioManager] Auto unlock listener added for events:', events);
  }

  /**
   * 获取当前使用的实现类型
   */
  getImplementationInfo(): {
    music: 'web-audio' | 'html5-audio';
    soundEffect: 'web-audio' | 'html5-audio';
  } {
    return {
      music: this.musicManager.getImplementationType(),
      soundEffect: this.soundEffectManager.getImplementationType(),
    };
  }

  /**
   * 是否全部使用 Web Audio API
   */
  isFullyWebAudio(): boolean {
    return this.musicManager.isUsingWebAudio() && this.soundEffectManager.isUsingWebAudio();
  }

  /**
   * 销毁音频管理器
   */
  destroy(): void {
    this.musicManager.stopMusic();
    this.soundEffectManager.stopAllSounds();

    // 如果使用 Web Audio，清理资源
    if (this.musicManager.isUsingWebAudio()) {
      this.musicManager.destroy();
    }
    if (this.soundEffectManager.isUsingWebAudio()) {
      this.soundEffectManager.destroy();
    }
  }
}

// 导出单例实例（可选，也可以不导出，让使用者自己创建）
let audioManagerInstance: UnifiedAudioManager | null = null;

/**
 * 获取或创建音频管理器单例
 */
export function getAudioManager(options?: UnifiedAudioManagerOptions): UnifiedAudioManager {
  if (!audioManagerInstance) {
    audioManagerInstance = new UnifiedAudioManager(options);
  }
  return audioManagerInstance;
}

/**
 * 重置音频管理器单例（用于测试或重新初始化）
 */
export function resetAudioManager(): void {
  if (audioManagerInstance) {
    audioManagerInstance.destroy();
    audioManagerInstance = null;
  }
}

